import { F, type Level, type Tile } from '../level/types';

/** Uniform-grid spatial hash over static tiles, with per-query de-duplication. */
export class Grid {
  private cells = new Map<number, number[]>();
  private stamp: Uint32Array;
  private q = 1;
  private static readonly SIZE = 96;
  private static readonly OFFSET = 1024;

  constructor(private tiles: Tile[]) {
    this.stamp = new Uint32Array(tiles.length);
    for (const t of tiles) {
      const [cx0, cy0, cx1, cy1] = this.range(t.x, t.y, t.x + t.w, t.y + t.h);
      for (let cy = cy0; cy <= cy1; cy++)
        for (let cx = cx0; cx <= cx1; cx++) {
          const k = cy * 4096 + cx;
          let arr = this.cells.get(k);
          if (!arr) this.cells.set(k, (arr = []));
          arr.push(t.id);
        }
    }
  }

  private range(x0: number, y0: number, x1: number, y1: number): [number, number, number, number] {
    const s = Grid.SIZE;
    const o = Grid.OFFSET;
    return [
      Math.floor((x0 + o) / s),
      Math.floor((y0 + o) / s),
      Math.floor((x1 + o) / s),
      Math.floor((y1 + o) / s),
    ];
  }

  query(x0: number, y0: number, x1: number, y1: number, out: Tile[] = []): Tile[] {
    out.length = 0;
    const q = ++this.q;
    const [cx0, cy0, cx1, cy1] = this.range(x0, y0, x1, y1);
    for (let cy = cy0; cy <= cy1; cy++)
      for (let cx = cx0; cx <= cx1; cx++) {
        const arr = this.cells.get(cy * 4096 + cx);
        if (!arr) continue;
        for (const id of arr) {
          if (this.stamp[id] === q) continue;
          this.stamp[id] = q;
          const t = this.tiles[id];
          if (t.x < x1 && t.x + t.w > x0 && t.y < y1 && t.y + t.h > y0) out.push(t);
        }
      }
    return out;
  }
}

export const TIMED = { period: 3.4, on: 2.0, warn: 0.6 };

/** Runtime rules on top of a static level: timed platforms, solidity queries. */
export class World {
  readonly grid: Grid;
  time = 0;
  private scratch: Tile[] = [];

  constructor(readonly level: Level) {
    this.grid = new Grid(level.tiles);
  }

  /** 0 = on, 1 = warning (still solid), 2 = off. */
  timedState(t: Tile): 0 | 1 | 2 {
    const p = (((this.time / TIMED.period + (t.phase ?? 0)) % 1) + 1) % 1;
    const s = p * TIMED.period;
    return s < TIMED.on ? 0 : s < TIMED.on + TIMED.warn ? 1 : 2;
  }

  present(t: Tile): boolean {
    return !(t.flags & F.TIMED) || this.timedState(t) !== 2;
  }

  solid(t: Tile): boolean {
    return (t.flags & F.SOLID) !== 0 && this.present(t);
  }

  oneway(t: Tile): boolean {
    return (t.flags & F.ONEWAY) !== 0 && this.present(t);
  }

  query(x0: number, y0: number, x1: number, y1: number, out?: Tile[]): Tile[] {
    return this.grid.query(x0, y0, x1, y1, out ?? this.scratch);
  }

  /** Is there something to stand on at world point (x, y)? */
  floorAt(x: number, y: number): boolean {
    for (const t of this.query(x - 0.5, y - 0.5, x + 0.5, y + 0.5, []))
      if (this.solid(t) || this.oneway(t)) return true;
    return false;
  }

  solidRect(x: number, y: number, w: number, h: number, ignore?: Set<number>): boolean {
    for (const t of this.query(x, y, x + w, y + h, []))
      if (this.solid(t) && !ignore?.has(t.id)) return true;
    return false;
  }
}
